import operator from "../../legal/operator.json";

/** Who runs the service: named in the Privacy Policy, the children's notice and consent emails. */
export const OPERATOR: Record<keyof typeof operator, string> = operator;

/** True until legal/operator.json has real contact details (required in the children's notice). */
export const OPERATOR_INCOMPLETE = Object.values(operator).some((v) => /^\[.*\]$/.test(v));

/**
 * The documents every parent accepts. Changing a document's text means changing its sha256 here
 * (`npm run legal:hash`; tests/legal.test.ts checks it). Bump its version too when the change is
 * material: every parent is asked to accept the new version before they can continue.
 */
export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    path: "/privacy",
    file: "privacy-policy.md",
    version: "2026-11-01",
    sha256: "3cabbd643454ec7b3a3aa7aae49b641685ef525804cf4a94131d10c3d2c97e9f",
  },
  terms: {
    title: "Terms of Service",
    path: "/terms",
    file: "terms-of-service.md",
    version: "2026-10-31",
    sha256: "a151c9d44a2d7c133528eaab23926c51b77e92138738c37c257c5a6a597b3471",
  },
} as const;

export type LegalDoc = keyof typeof LEGAL_DOCS;

/** What a parent's acceptance is recorded against. */
export const LEGAL_VERSION = `privacy:${LEGAL_DOCS.privacy.version};terms:${LEGAL_DOCS.terms.version}`;

/**
 * The children's privacy notice parents consent to (COPPA direct notice). Bump only for a material
 * change in what we collect from kids or how we use or disclose it: every family must then give
 * consent again before their kids can keep signing in.
 */
export const CHILD_NOTICE_VERSION = "2026-11-01";

/** Retention schedule (Privacy Policy section 6). The retention job and the policy must agree. */
export const RETENTION = {
  /** Families whose trial or subscription ended. */
  lapsedDays: 90,
  /** Sign-ups that never started a plan. */
  abandonedSignupDays: 30,
  /** Kids' profiles in families that never gave parental consent. */
  unconsentedKidsDays: 30,
  /** Families where nobody has signed in. */
  unusedDays: 730,
  /** Parents are emailed this long before an account is deleted. */
  warningDays: 14,
  notificationDays: 180,
  auditDays: 365,
} as const;

/** Text from legal/*.md with the operator's details filled in. */
export function fillOperator(markdown: string): string {
  return markdown.replace(/\{\{(\w+)\}\}/g, (all, key: string) => (key in OPERATOR ? OPERATOR[key as keyof typeof OPERATOR] : all));
}
